/**
 * Zeomart Marketplace Vanilla JS Motion & Interaction Engine
 * Handles Carousels, Scroll Reveals, Entrance Sequences, Tabs, Countdowns, Accordions, Scroll Progress, Back-to-Top, and Ajax Cart
 */

(function () {
  'use strict';

  // ===== AJAX ADD TO CART HELPER WITH MOTION FEEDBACK =====
  window.zmAddToCart = function (form) {
    const submitBtn = form.querySelector('button[type="submit"]');
    if (submitBtn) {
      submitBtn.classList.add('loading');
    }

    const formData = new FormData(form);
    fetch(window.Shopify?.routes?.root + 'cart/add.js', {
      method: 'POST',
      body: formData,
    })
      .then((response) => response.json())
      .then((data) => {
        if (submitBtn) {
          submitBtn.classList.remove('loading');
          submitBtn.classList.add('added');
          setTimeout(() => submitBtn.classList.remove('added'), 1600);
        }
        // Update cart counter in header with pop animation
        fetch(window.Shopify?.routes?.root + 'cart.js')
          .then((res) => res.json())
          .then((cart) => {
            document.querySelectorAll('.zm-tool-badge').forEach((badge) => {
              badge.textContent = cart.item_count;
              badge.classList.remove('popping');
              void badge.offsetWidth; // Trigger reflow for animation restart
              badge.classList.add('popping');
              setTimeout(() => badge.classList.remove('popping'), 500);
            });
            const cartDrawer = document.querySelector('cart-drawer');
            if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
              cartDrawer.renderContents(cart);
              cartDrawer.open();
            }
          })
          .catch(() => {});
      })
      .catch((error) => {
        console.error('Error adding to cart:', error);
        if (submitBtn) submitBtn.classList.remove('loading');
      });
  };

  // ===== UNIFIED SCROLL REVEAL (INTERSECTION OBSERVER) =====
  let revealObserver;
  function initScrollReveal() {
    if (typeof IntersectionObserver === 'undefined') {
      document.querySelectorAll('.zm-reveal-up, .zm-reveal-fade, .zm-reveal-scale, .zm-reveal-left, .zm-reveal-right, .zm-stagger').forEach((el) => {
        el.classList.add('is-visible');
      });
      return;
    }

    if (revealObserver) {
      revealObserver.disconnect();
    }

    revealObserver = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -40px 0px',
      }
    );

    document.querySelectorAll('.zm-reveal-up, .zm-reveal-fade, .zm-reveal-scale, .zm-reveal-left, .zm-reveal-right, .zm-stagger').forEach((el) => {
      const rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) {
        el.classList.add('is-visible');
      } else {
        revealObserver.observe(el);
      }
    });
  }

  // ===== SCROLL PROGRESS & BACK TO TOP BUTTON =====
  function initScrollProgressAndBackToTop() {
    const progressBar = document.querySelector('.zm-scroll-progress');
    let backToTopBtn = document.querySelector('.zm-back-to-top');

    if (!backToTopBtn) {
      backToTopBtn = document.createElement('button');
      backToTopBtn.className = 'zm-back-to-top';
      backToTopBtn.setAttribute('aria-label', 'Back to top');
      backToTopBtn.innerHTML = `
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
          <polyline points="18 15 12 9 6 15"></polyline>
        </svg>
      `;
      document.body.appendChild(backToTopBtn);

      backToTopBtn.addEventListener('click', () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    }

    let ticking = false;
    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const scrollTop = window.scrollY || document.documentElement.scrollTop;
          const docHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
          const scrollPercent = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;

          if (progressBar) {
            progressBar.style.width = scrollPercent + '%';
          }

          if (backToTopBtn) {
            if (scrollTop > 350) {
              backToTopBtn.classList.add('visible');
            } else {
              backToTopBtn.classList.remove('visible');
            }
          }

          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });
  }

  // ===== CAROUSEL ENGINE (Container Scroll & Dynamic Step) =====
  function initCarousel(wrapper) {
    if (wrapper.__zmCarouselInit) {
      if (typeof wrapper.__zmUpdateArrowStates === 'function') {
        wrapper.__zmUpdateArrowStates();
      }
      return;
    }
    wrapper.__zmCarouselInit = true;

    const trackContainer = wrapper.querySelector('.zm-carousel-track-container');
    const track = wrapper.querySelector('.zm-carousel-track');
    const slides = wrapper.querySelectorAll('.zm-carousel-slide');
    const prevBtn = wrapper.querySelector('.zm-carousel-arrow--prev');
    const nextBtn = wrapper.querySelector('.zm-carousel-arrow--next');
    const dotsContainer = wrapper.querySelector('.zm-carousel-dots');

    if (!trackContainer || !track || slides.length === 0) return;

    // Reset any legacy transform inline style
    track.style.transform = 'none';

    function getGap() {
      try {
        const computedGap = parseFloat(window.getComputedStyle(track).gap || window.getComputedStyle(track).columnGap);
        if (!isNaN(computedGap) && computedGap >= 0) return computedGap;
      } catch (e) {}
      if (window.innerWidth >= 1024) return 20;
      if (window.innerWidth >= 768) return 16;
      return 12;
    }

    function getScrollStep() {
      if (!slides || slides.length === 0 || !trackContainer) return 300;
      const firstSlide = slides[0];
      const slideWidth = firstSlide.getBoundingClientRect().width;
      const gap = getGap();
      const containerWidth = trackContainer.clientWidth;
      const visibleItems = Math.max(1, Math.floor(containerWidth / (slideWidth + gap)));
      return (slideWidth + gap) * visibleItems;
    }

    function updateArrowStates() {
      if (!trackContainer) return;
      const scrollLeft = trackContainer.scrollLeft;
      const maxScrollLeft = trackContainer.scrollWidth - trackContainer.clientWidth;
      const tolerance = 6;

      const isAtStart = scrollLeft <= tolerance;
      const isAtEnd = scrollLeft >= (maxScrollLeft - tolerance) || maxScrollLeft <= tolerance;

      if (prevBtn) {
        prevBtn.disabled = isAtStart;
        prevBtn.classList.toggle('is-disabled', isAtStart);
        prevBtn.setAttribute('aria-disabled', isAtStart ? 'true' : 'false');
      }

      if (nextBtn) {
        nextBtn.disabled = isAtEnd;
        nextBtn.classList.toggle('is-disabled', isAtEnd);
        nextBtn.setAttribute('aria-disabled', isAtEnd ? 'true' : 'false');
      }

      if (dotsContainer && slides.length > 0) {
        const slideWidth = slides[0].getBoundingClientRect().width;
        const gap = getGap();
        const step = slideWidth + gap;
        const activeIndex = Math.min(
          dotsContainer.children.length - 1,
          Math.max(0, Math.round(scrollLeft / (step || 1)))
        );
        const dots = dotsContainer.querySelectorAll('.zm-carousel-dot');
        dots.forEach((dot, idx) => {
          dot.classList.toggle('active', idx === activeIndex);
        });
      }
    }

    wrapper.__zmUpdateArrowStates = updateArrowStates;

    if (prevBtn) {
      prevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        const step = getScrollStep();
        trackContainer.scrollBy({ left: -step, behavior: 'smooth' });
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        const step = getScrollStep();
        trackContainer.scrollBy({ left: step, behavior: 'smooth' });
      });
    }

    // Passive scroll listener with requestAnimationFrame for smooth 60fps tracking
    let scrollTicking = false;
    trackContainer.addEventListener('scroll', () => {
      if (!scrollTicking) {
        window.requestAnimationFrame(() => {
          updateArrowStates();
          scrollTicking = false;
        });
        scrollTicking = true;
      }
    }, { passive: true });

    window.addEventListener('resize', () => {
      updateArrowStates();
    });

    // Build dots if container present
    if (dotsContainer) {
      dotsContainer.innerHTML = '';
      const containerWidth = trackContainer.clientWidth || 300;
      const firstSlideWidth = slides[0].getBoundingClientRect().width || 150;
      const gap = getGap();
      const visibleItems = Math.max(1, Math.floor(containerWidth / (firstSlideWidth + gap)));
      const dotCount = Math.min(6, Math.max(1, slides.length - visibleItems + 1));

      for (let i = 0; i < dotCount; i++) {
        const dot = document.createElement('button');
        dot.className = 'zm-carousel-dot' + (i === 0 ? ' active' : '');
        dot.setAttribute('aria-label', `Slide ${i + 1}`);
        dot.addEventListener('click', (e) => {
          e.preventDefault();
          const slideWidth = slides[0].getBoundingClientRect().width;
          const currentGap = getGap();
          trackContainer.scrollTo({ left: i * (slideWidth + currentGap), behavior: 'smooth' });
        });
        dotsContainer.appendChild(dot);
      }
    }

    // Initial arrow state calculation
    setTimeout(updateArrowStates, 60);
  }

  // ===== COUNTDOWN TIMER ENGINE WITH NUMBER TRANSITIONS =====
  function initCountdown(timerEl) {
    if (timerEl.__zmCountdownInit) return;
    timerEl.__zmCountdownInit = true;

    const endDateStr = timerEl.dataset.endDate;
    let targetDate;
    if (endDateStr) {
      targetDate = new Date(endDateStr).getTime();
    } else {
      targetDate = Date.now() + (178 * 24 * 60 * 60 * 1000) + (3 * 60 * 60 * 1000) + (14 * 60 * 1000);
    }

    const daysEl = timerEl.querySelector('.zm-days-val');
    const hoursEl = timerEl.querySelector('.zm-hours-val');
    const minsEl = timerEl.querySelector('.zm-mins-val');
    const secsEl = timerEl.querySelector('.zm-secs-val');

    function updateVal(el, newVal) {
      if (!el) return;
      const formatted = String(newVal).padStart(2, '0');
      if (el.textContent !== formatted) {
        el.textContent = formatted;
        el.classList.remove('digit-tick');
        void el.offsetWidth;
        el.classList.add('digit-tick');
      }
    }

    function tick() {
      const now = Date.now();
      const distance = targetDate - now;

      if (distance <= 0) {
        updateVal(daysEl, 0);
        updateVal(hoursEl, 0);
        updateVal(minsEl, 0);
        updateVal(secsEl, 0);
        return;
      }

      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % 1000) / 1000);

      updateVal(daysEl, days);
      updateVal(hoursEl, hours);
      updateVal(minsEl, minutes);
      updateVal(secsEl, seconds);
    }

    tick();
    setInterval(tick, 1000);
  }

  // ===== TAB SWITCHER ENGINE WITH SMOOTH TRANSITION =====
  function initTabs(section) {
    const tabBtns = section.querySelectorAll('.zm-tab-btn');
    const tabPanes = section.querySelectorAll('.zm-tab-pane');

    tabBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        const targetId = btn.dataset.tabTarget;
        tabBtns.forEach((b) => b.classList.remove('active'));
        btn.classList.add('active');

        tabPanes.forEach((pane) => {
          if (pane.id === targetId) {
            pane.style.display = 'block';
            const carousel = pane.querySelector('.zm-carousel-wrapper');
            if (carousel) {
              if (carousel.__zmCarouselInit && typeof carousel.__zmUpdateArrowStates === 'function') {
                setTimeout(() => carousel.__zmUpdateArrowStates(), 50);
              } else {
                initCarousel(carousel);
              }
            }
          } else {
            pane.style.display = 'none';
          }
        });
      });
    });
  }

  // ===== MOBILE FOOTER ACCORDIONS =====
  function initFooterAccordions() {
    document.querySelectorAll('.zm-footer-col__title--accordion').forEach((title) => {
      title.addEventListener('click', () => {
        if (window.innerWidth <= 768) {
          title.parentElement.classList.toggle('active');
        }
      });
    });
  }

  // ===== GLOBAL INIT =====
  function initAll() {
    document.querySelectorAll('.zm-carousel-wrapper').forEach(initCarousel);
    document.querySelectorAll('.zm-countdown-timer').forEach(initCountdown);
    document.querySelectorAll('[data-zm-tabs]').forEach(initTabs);
    initFooterAccordions();
    initScrollReveal();
    initScrollProgressAndBackToTop();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();
